import { useCallback, useEffect, useState } from "react"
import { supabase } from "@/lib/supabase"
import { useOrgStore } from "@/stores/orgStore"
import type { PermissionKey } from "@/lib/permissions"

/**
 * Returns whether the current user has the given permission in the current org.
 * Uses RPC has_permission(org_id, permission_key) for server-side consistency.
 */
export function usePermission(permission: PermissionKey): {
  hasPermission: boolean
  loading: boolean
} {
  const { currentOrg } = useOrgStore()
  const [hasPerm, setHasPerm] = useState(false)
  const [loading, setLoading] = useState(true)

  const check = useCallback(async () => {
    if (!currentOrg?.id) {
      setHasPerm(false)
      setLoading(false)
      return
    }
    setLoading(true)
    const { data, error } = await supabase.rpc("has_permission", {
      org_uuid: currentOrg.id,
      permission_key: permission,
    })
    if (error) {
      console.error("usePermission error:", error)
      setHasPerm(false)
    } else {
      setHasPerm(data === true)
    }
    setLoading(false)
  }, [currentOrg?.id, permission])

  useEffect(() => {
    check()
  }, [check])

  return { hasPermission: hasPerm, loading }
}
